import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AiOutlineFilePdf, AiOutlinePrinter } from "react-icons/ai";
import { BsFiletypeXls } from "react-icons/bs";
import { FaBalanceScale } from "react-icons/fa";
import { FiArrowLeft } from "react-icons/fi";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { errorAlert } from "../../Components/Alerts";
import ReusableAccountBooksGrid from "../../Components/ReusableAccountBooksGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";
import { AddActionsButton } from "../../Components/AddElementsButton";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import {
    financialColumns,
    financialDetailColumns,
    financialDetailMappings,
    financialMappings,
} from "../../Components/ExcelConverter/FinancialStatement";
import { getActionTypeLabel } from "../../OtherData/actionTypeMap";
import "../AccountingReportsCRUD/AccountsReportPage.scss";
import "../../Style/__button.scss";

const currencyFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));
const formatUnsignedCurrency = (value) => formatCurrency(Math.abs(Number(value || 0)));

function formatFinancialStatement(value) {
    const normalized = String(value || "").trim().toUpperCase();

    if (normalized === "BG") {
        return "BG - Bilanci i Gjendjes";
    }

    if (normalized === "PA") {
        return "PA - Pasqyra e te Ardhurave";
    }

    return String(value || "");
}

function normalizeQuickFilterText(value) {
    if (Array.isArray(value)) {
        return value.join(" ").trim().toLowerCase();
    }

    return String(value || "").trim().toLowerCase();
}

function parseDateToTime(value) {
    const raw = String(value || "").trim();
    if (!raw) {
        return null;
    }

    const dottedMatch = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (dottedMatch) {
        const [, day, month, year] = dottedMatch;
        return new Date(`${year}-${month}-${day}T00:00:00`).getTime();
    }

    const slashMatch = raw.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
    if (slashMatch) {
        const [, year, month, day] = slashMatch;
        return new Date(`${year}-${month}-${day}T00:00:00`).getTime();
    }

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return new Date(`${year}-${month}-${day}T00:00:00`).getTime();
    }

    const parsedDate = new Date(raw);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.getTime();
}

function formatDateForDisplay(date) {
    if (!date) {
        return "";
    }

    if (date instanceof Date) {
        if (Number.isNaN(date.getTime())) {
            return "";
        }

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }

    const raw = String(date).trim();
    if (!raw) {
        return "";
    }

    const dottedMatch = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (dottedMatch) {
        return raw;
    }

    const slashMatch = raw.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
    if (slashMatch) {
        const [, year, month, day] = slashMatch;
        return `${day}.${month}.${year}`;
    }

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return `${day}.${month}.${year}`;
    }

    const parsedDate = new Date(raw);
    if (Number.isNaN(parsedDate.getTime())) {
        return raw;
    }

    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = parsedDate.getFullYear();
    return `${day}.${month}.${year}`;
}

function applyLegacyGridState(items, options) {
    const {
        quickFilterValue,
        quickFields,
        filterItems,
        sortModel,
        paginationModel,
        getFieldValue,
        numericFields,
        dateFields,
    } = options;

    const searchText = normalizeQuickFilterText(quickFilterValue);
    const searchTerms = searchText ? searchText.split(/\s+/).filter(Boolean) : [];

    let workingSet = Array.isArray(items) ? [...items] : [];

    if (searchTerms.length > 0) {
        workingSet = workingSet.filter((item, index) => {
            const haystack = quickFields
                .map((field) => String(getFieldValue(item, field, index) ?? "").toLowerCase())
                .join(" ");

            return searchTerms.every((term) => haystack.includes(term));
        });
    }

    const activeFilters = (filterItems || []).filter((filterItem) => (
        filterItem?.field && String(filterItem.value ?? "").trim() !== ""
    ));

    if (activeFilters.length > 0) {
        workingSet = workingSet.filter((item, index) => activeFilters.every((filterItem) => {
            const field = filterItem.field;
            const operator = filterItem.operator || "contains";
            const rawValue = getFieldValue(item, field, index);
            const filterValue = String(filterItem.value ?? "").trim();

            if (dateFields.has(field)) {
                const rowTime = parseDateToTime(rawValue);
                const filterTime = parseDateToTime(filterValue);
                if (rowTime === null || filterTime === null) {
                    return false;
                }

                switch (operator) {
                    case "gte":
                        return rowTime >= filterTime;
                    case "gt":
                        return rowTime > filterTime;
                    case "lte":
                        return rowTime <= filterTime;
                    case "lt":
                        return rowTime < filterTime;
                    case "equals":
                        return rowTime === filterTime;
                    default:
                        return String(rawValue ?? "").toLowerCase().includes(filterValue.toLowerCase());
                }
            }

            if (numericFields.has(field)) {
                const rowNumber = Number(rawValue ?? 0);
                const filterNumber = Number(filterValue.replace(/,/g, "."));
                if (!Number.isFinite(filterNumber)) {
                    return false;
                }

                switch (operator) {
                    case "gte":
                        return rowNumber >= filterNumber;
                    case "gt":
                        return rowNumber > filterNumber;
                    case "lte":
                        return rowNumber <= filterNumber;
                    case "lt":
                        return rowNumber < filterNumber;
                    case "equals":
                        return rowNumber === filterNumber;
                    default:
                        return String(rawValue ?? "").includes(filterValue);
                }
            }

            const normalizedValue = String(rawValue ?? "").toLowerCase();
            const normalizedFilter = filterValue.toLowerCase();

            switch (operator) {
                case "equals":
                    return normalizedValue === normalizedFilter;
                case "contains":
                default:
                    return normalizedValue.includes(normalizedFilter);
            }
        }));
    }

    const filteredItems = workingSet;
    const sortItem = (sortModel || [])[0];

    if (sortItem?.field && sortItem?.sort) {
        const direction = sortItem.sort === "desc" ? -1 : 1;
        filteredItems.sort((leftItem, rightItem) => {
            const leftValue = getFieldValue(leftItem, sortItem.field, 0);
            const rightValue = getFieldValue(rightItem, sortItem.field, 0);

            if (dateFields.has(sortItem.field)) {
                const leftTime = parseDateToTime(leftValue) ?? 0;
                const rightTime = parseDateToTime(rightValue) ?? 0;
                return (leftTime - rightTime) * direction;
            }

            if (numericFields.has(sortItem.field)) {
                return (Number(leftValue ?? 0) - Number(rightValue ?? 0)) * direction;
            }

            return String(leftValue ?? "").localeCompare(String(rightValue ?? ""), "sq", { sensitivity: "base" }) * direction;
        });
    }

    if (!paginationModel) {
        return {
            filteredItems,
            pagedItems: filteredItems,
            totalRows: filteredItems.length,
        };
    }

    const page = Number(paginationModel.page) || 0;
    const pageSize = Number(paginationModel.pageSize) || 50;
    const startIndex = page * pageSize;
    const pagedItems = filteredItems.slice(startIndex, startIndex + pageSize);

    return {
        filteredItems,
        pagedItems,
        totalRows: filteredItems.length,
    };
}

function getMainFieldValue(item, field, index) {
    switch (field) {
        case "rowNumber":
            return index + 1;
        case "ledgerName":
            return item?.LedgerName || "";
        case "debit":
            return Number(item?.Debit || 0);
        case "credit":
            return Number(item?.Credit || 0);
        case "balance":
            return Math.abs(Number(item?.Balance || 0));
        case "bilanceSide":
            return item?.BilanceSide || "";
        case "financialStatement":
            return formatFinancialStatement(item?.FinancialStatements || "");
        default:
            return item?.[field];
    }
}

function getDetailsFieldValue(item, field, index) {
    switch (field) {
        case "rowNumber":
            return index + 1;
        case "type":
            return getActionTypeLabel(item?.ActionType || "");
        case "ledgerNameSQ":
            return item?.LedgerNameSQ || "";
        case "comment":
            return item?.Comment || "";
        case "date":
            return formatDateForDisplay(item?.Date);
        case "debit":
            return Number(item?.Debit || 0);
        case "credit":
            return Number(item?.Credit || 0);
        case "progressive":
            return Number(item?.Progressive || 0);
        default:
            return item?.[field];
    }
}

export default function TrialBalancePage() {
    const headers = authHeader();
    const { fiscalYear } = useContext(FiscalYearContext);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [quickFilter, setQuickFilter] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [totals, setTotals] = useState({ totalDebit: 0, totalCredit: 0, totalBalance: 0 });

    const [companiesData, setCompaniesData] = useState([]);
    const [logoPath, setLogoPath] = useState("");

    const [showDetails, setShowDetails] = useState(false);
    const [selectedLedgerId, setSelectedLedgerId] = useState(null);
    const [accountDetails, setAccountDetails] = useState([]);
    const [accountDetailName, setAccountDetailName] = useState("");
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsPaginationModel, setDetailsPaginationModel] = useState({ page: 0, pageSize: 100 });
    const [detailsSortModel, setDetailsSortModel] = useState([]);
    const [detailsFilterModel, setDetailsFilterModel] = useState({ items: [] });
    const [detailsQuickFilter, setDetailsQuickFilter] = useState([]);
    const [detailsRowCount, setDetailsRowCount] = useState(0);
    const [detailsTotals, setDetailsTotals] = useState({ debit: 0, credit: 0, balance: 0 });

    const buildParams = (page, pageSize, sort, filter, quick) => ({
        fiscalYear,
        page,
        pageSize,
        sort: JSON.stringify(sort || []),
        filter: JSON.stringify(filter || []),
        quickFilter: (quick || []).join(" "),
    });

    const fetchCompaniesData = async () => {
        try {
            const response = await axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers });
            const companyItems = Array.isArray(response?.data) ? response.data : [];
            setCompaniesData(companyItems);
            setLogoPath(companyItems[0]?.CompanyLogo ? ApiLink.PHOTO_URL + companyItems[0].CompanyLogo : "");
        } catch (error) {
            if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave të kompanisë: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(ApiLink.API_URL + "Reports", {
                headers,
                params: buildParams(
                    paginationModel.page,
                    paginationModel.pageSize,
                    sortModel,
                    filterModel.items || [],
                    quickFilter,
                ),
            });

            const payload = response?.data;
            const rawItems = Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
            const usesLegacyResponse = Array.isArray(payload);
            const legacyState = usesLegacyResponse
                ? applyLegacyGridState(rawItems, {
                    quickFilterValue: quickFilter,
                    quickFields: ["ledgerName", "debit", "credit", "balance", "bilanceSide", "financialStatement"],
                    filterItems: filterModel.items || [],
                    sortModel,
                    paginationModel,
                    getFieldValue: getMainFieldValue,
                    numericFields: new Set(["rowNumber", "debit", "credit", "balance"]),
                    dateFields: new Set(),
                })
                : null;

            const items = usesLegacyResponse ? legacyState.pagedItems : rawItems;
            const total = usesLegacyResponse
                ? legacyState.totalRows
                : Number(payload?.total ?? payload?.totalRows ?? items.length);
            const payloadTotals = payload?.totals || {};

            const fallbackTotals = (usesLegacyResponse ? legacyState.filteredItems : []).reduce((accumulator, item) => ({
                totalDebit: accumulator.totalDebit + Number(item?.Debit || 0),
                totalCredit: accumulator.totalCredit + Number(item?.Credit || 0),
                totalBalance: accumulator.totalBalance + Number(item?.Balance || 0),
            }), { totalDebit: 0, totalCredit: 0, totalBalance: 0 });

            setData(items);
            setRowCount(total);
            setTotals({
                totalDebit: usesLegacyResponse
                    ? fallbackTotals.totalDebit
                    : Number(payloadTotals.TotalDebit ?? payloadTotals.totalDebit ?? 0),
                totalCredit: usesLegacyResponse
                    ? fallbackTotals.totalCredit
                    : Number(payloadTotals.TotalCredit ?? payloadTotals.totalCredit ?? 0),
                totalBalance: usesLegacyResponse
                    ? fallbackTotals.totalBalance
                    : Number(payloadTotals.TotalBalance ?? payloadTotals.totalBalance ?? 0),
            });
        } catch (error) {
            if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [fiscalYear, paginationModel, sortModel, filterModel, quickFilter]);

    useEffect(() => {
        fetchCompaniesData();
    }, [fiscalYear]);

    const handleViewDetails = (rowPayload) => {
        const ledgerId = rowPayload?.LedgerID || rowPayload?.rowInfo?.LedgerID;
        if (!ledgerId) {
            return;
        }

        setSelectedLedgerId(ledgerId);
        setAccountDetailName(rowPayload?.LedgerName || rowPayload?.rowInfo?.LedgerName || "");
        setDetailsPaginationModel({ page: 0, pageSize: 100 });
        setDetailsSortModel([]);
        setDetailsFilterModel({ items: [] });
        setDetailsQuickFilter([]);
        setShowDetails(true);
    };

    const fetchDetailsData = async () => {
        if (!showDetails || !selectedLedgerId) {
            return;
        }

        setDetailsLoading(true);
        try {
            const response = await axios.get(ApiLink.API_URL + "Reports/TrialBalanceDetails", {
                headers,
                params: {
                    ledgerID: selectedLedgerId,
                    fiscalYear,
                    page: detailsPaginationModel.page,
                    pageSize: detailsPaginationModel.pageSize,
                    sort: JSON.stringify(detailsSortModel),
                    filter: JSON.stringify(detailsFilterModel.items || []),
                    quickFilter: detailsQuickFilter.join(" "),
                },
            });

            const payload = response?.data;
            const rawItems = Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
            const usesLegacyResponse = Array.isArray(payload);
            const legacyState = usesLegacyResponse
                ? applyLegacyGridState(rawItems, {
                    quickFilterValue: detailsQuickFilter,
                    quickFields: ["type", "ledgerNameSQ", "comment", "date", "debit", "credit", "progressive"],
                    filterItems: detailsFilterModel.items || [],
                    sortModel: detailsSortModel,
                    paginationModel: detailsPaginationModel,
                    getFieldValue: getDetailsFieldValue,
                    numericFields: new Set(["rowNumber", "debit", "credit", "progressive"]),
                    dateFields: new Set(["date"]),
                })
                : null;

            const items = usesLegacyResponse ? legacyState.pagedItems : rawItems;
            const total = usesLegacyResponse
                ? legacyState.totalRows
                : Number(payload?.total ?? payload?.totalRows ?? items.length);
            const totalsPayload = payload?.totals || {};

            const fallbackTotals = (usesLegacyResponse ? legacyState.filteredItems : []).reduce((accumulator, item) => ({
                debit: accumulator.debit + Number(item?.Debit || 0),
                credit: accumulator.credit + Number(item?.Credit || 0),
                balance: accumulator.balance + (Number(item?.Debit || 0) - Number(item?.Credit || 0)),
            }), { debit: 0, credit: 0, balance: 0 });

            setAccountDetails(items);
            setDetailsRowCount(total);
            setDetailsTotals({
                debit: usesLegacyResponse
                    ? fallbackTotals.debit
                    : Number(totalsPayload.TotalDebit ?? totalsPayload.totalDebit ?? 0),
                credit: usesLegacyResponse
                    ? fallbackTotals.credit
                    : Number(totalsPayload.TotalCredit ?? totalsPayload.totalCredit ?? 0),
                balance: usesLegacyResponse
                    ? fallbackTotals.balance
                    : Number(totalsPayload.TotalBalance ?? totalsPayload.totalBalance ?? 0),
            });

            if (!accountDetailName && items.length > 0) {
                setAccountDetailName(items[0]?.LedgerNameSQ || "");
            }
        } catch (error) {
            if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e detajeve: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë.");
            }
        } finally {
            setDetailsLoading(false);
        }
    };

    useEffect(() => {
        fetchDetailsData();
    }, [
        showDetails,
        selectedLedgerId,
        fiscalYear,
        detailsPaginationModel,
        detailsSortModel,
        detailsFilterModel,
        detailsQuickFilter,
    ]);

    const fetchAllRows = async () => {
        const totalRowsToExport = Math.max(Number(rowCount) || 0, 1);
        const response = await axios.get(ApiLink.API_URL + "Reports", {
            headers,
            params: buildParams(0, totalRowsToExport, sortModel, filterModel.items || [], quickFilter),
        });

        const payload = response?.data;
        const rawItems = Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
        if (!Array.isArray(payload)) {
            return rawItems;
        }

        const legacyState = applyLegacyGridState(rawItems, {
            quickFilterValue: quickFilter,
            quickFields: ["ledgerName", "debit", "credit", "balance", "bilanceSide", "financialStatement"],
            filterItems: filterModel.items || [],
            sortModel,
            paginationModel: null,
            getFieldValue: getMainFieldValue,
            numericFields: new Set(["rowNumber", "debit", "credit", "balance"]),
            dateFields: new Set(),
        });

        return legacyState.filteredItems;
    };

    const fetchAllDetailsRows = async () => {
        if (!selectedLedgerId) {
            return [];
        }

        const totalRowsToExport = Math.max(Number(detailsRowCount) || 0, 1);
        const response = await axios.get(ApiLink.API_URL + "Reports/TrialBalanceDetails", {
            headers,
            params: {
                ledgerID: selectedLedgerId,
                fiscalYear,
                page: 0,
                pageSize: totalRowsToExport,
                sort: JSON.stringify(detailsSortModel),
                filter: JSON.stringify(detailsFilterModel.items || []),
                quickFilter: detailsQuickFilter.join(" "),
            },
        });

        const payload = response?.data;
        const rawItems = Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
        if (!Array.isArray(payload)) {
            return rawItems;
        }

        const legacyState = applyLegacyGridState(rawItems, {
            quickFilterValue: detailsQuickFilter,
            quickFields: ["type", "ledgerNameSQ", "comment", "date", "debit", "credit", "progressive"],
            filterItems: detailsFilterModel.items || [],
            sortModel: detailsSortModel,
            paginationModel: null,
            getFieldValue: getDetailsFieldValue,
            numericFields: new Set(["rowNumber", "debit", "credit", "progressive"]),
            dateFields: new Set(["date"]),
        });

        return legacyState.filteredItems;
    };

    const mapMainExportData = (items) => (
        items.map((item) => ({
            LedgerID: item.LedgerID || "",
            LedgerName: item.LedgerName || "",
            FinancialStatements: formatFinancialStatement(item.FinancialStatements || ""),
            BilanceSide: item.BilanceSide || "",
            Debit: Number(item.Debit || 0),
            Credit: Number(item.Credit || 0),
            Balance: Math.abs(Number(item.Balance || 0)),
        }))
    );

    const mapDetailsExportData = (items) => (
        items.map((item) => ({
                ActionType: getActionTypeLabel(item.ActionType || ""),
            LedgerNameSQ: item.LedgerNameSQ || "",
            Comment: item.Comment || "",
            Date: formatDateForDisplay(item.Date),
            Debit: Number(item.Debit || 0),
            Credit: Number(item.Credit || 0),
            Progressive: Number(item.Progressive || 0),
        }))
    );

    const exportDatasetToExcel = (items, columns, mappings, fileName) => {
        if (!items || items.length === 0) {
            errorAlert("Nuk ka të dhëna për eksport.");
            return;
        }

        const filteredData = items.map((item) => (
            columns.reduce((accumulator, column) => {
                accumulator[mappings[column] || column] = item[column];
                return accumulator;
            }, {})
        ));

        const worksheet = XLSX.utils.json_to_sheet(filteredData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const excelFile = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(excelFile, `${fileName}.xlsx`);
    };

    const reportPeriodLabel = useMemo(() => `${fiscalYear}`, [fiscalYear]);

    const sanitizeFileName = (value, fallback) => {
        const cleaned = String(value || "")
            .trim()
            .replace(/[\\/:*?"<>|]/g, "")
            .replace(/\s+/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_|_$/g, "");

        return cleaned || fallback;
    };

    const addLogo = (doc) => {
        if (!logoPath) {
            return;
        }

        doc.addImage(logoPath, "JPEG", 12, 10, 24, 24);
    };

    const addReportHeader = (doc, title, subtitle, periodLabel, rightMetaLabel = "") => {
        const company = companiesData[0];
        const pageWidth = doc.internal.pageSize.getWidth();

        addLogo(doc);

        doc.setDrawColor(40, 73, 69);
        doc.setLineWidth(0.5);
        doc.line(12, 38, pageWidth - 12, 38);

        doc.setTextColor(31, 58, 54);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text(title, 40, 18);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(95, 127, 115);
        doc.text(subtitle, 40, 24);

        doc.setFontSize(9);
        doc.setTextColor(31, 58, 54);
        doc.text(`Periudha: ${periodLabel}`, 12, 46);
        if (rightMetaLabel) {
            doc.setFont("helvetica", "bold");
            doc.text(rightMetaLabel, pageWidth - 12, 46, { align: "right" });
        }

        if (!company) {
            return 52;
        }

        const companyDetails = [
            company.CompanyName,
            `Numri Identifikues: ${company.IdentificationNumber || "-"}`,
            `Numri i TVSH-së: ${company.VATNumber || "-"}`,
            `Adresa: ${company.Address || "-"}`,
            `Tel: ${company.PhoneNumbers || "-"}`,
            `Email: ${company.Email || "-"}`,
        ];

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(31, 58, 54);
        doc.text(companyDetails[0], pageWidth - 12, 16, { align: "right" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(95, 127, 115);
        doc.text(companyDetails.slice(1), pageWidth - 12, 22, { align: "right" });

        return 52;
    };

    const addReportFooter = (doc, periodLabel, generatedOnLabel) => {
        const pageCount = doc.getNumberOfPages();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const brandingLabel = "© www.accountree.net";
        const footerCenterLabel = `Gjeneruar më: ${generatedOnLabel} | ${brandingLabel}`;

        for (let page = 1; page <= pageCount; page += 1) {
            doc.setPage(page);
            doc.setDrawColor(212, 226, 219);
            doc.setLineWidth(0.3);
            doc.line(12, pageHeight - 10, pageWidth - 12, pageHeight - 10);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(95, 127, 115);
            doc.text(`Periudha: ${periodLabel}`, 12, pageHeight - 5.5);
            doc.text(footerCenterLabel, pageWidth / 2, pageHeight - 5.5, { align: "center" });
            doc.text(`Faqe ${page} / ${pageCount}`, pageWidth - 12, pageHeight - 5.5, { align: "right" });
        }
    };

    const addPdfTable = (doc, headersRow, bodyRows, footRow, startY, baseColumnStyles) => {
        const horizontalMargin = 12;
        const availableTableWidth = doc.internal.pageSize.getWidth() - (horizontalMargin * 2);
        const baseTableWidth = Object.values(baseColumnStyles).reduce(
            (sum, style) => sum + Number(style.cellWidth || 0),
            0,
        );
        const scaleRatio = baseTableWidth > 0 ? availableTableWidth / baseTableWidth : 1;
        const columnStyles = Object.entries(baseColumnStyles).reduce((accumulator, [key, style]) => {
            accumulator[key] = {
                ...style,
                cellWidth: Number((Number(style.cellWidth || 0) * scaleRatio).toFixed(3)),
            };
            return accumulator;
        }, {});

        doc.autoTable({
            head: [headersRow],
            body: bodyRows,
            foot: [footRow],
            startY,
            showHead: "firstPage",
            showFoot: "lastPage",
            margin: {
                top: 16,
                left: horizontalMargin,
                right: horizontalMargin,
                bottom: 16,
            },
            theme: "grid",
            styles: {
                fontSize: 7.5,
                cellPadding: { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 },
                lineColor: [212, 226, 219],
                lineWidth: 0.1,
                textColor: [31, 58, 54],
                overflow: "linebreak",
            },
            headStyles: {
                fillColor: [40, 73, 69],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                halign: "center",
                valign: "middle",
            },
            bodyStyles: {
                valign: "middle",
            },
            footStyles: {
                fillColor: [231, 240, 235],
                textColor: [31, 58, 54],
                fontStyle: "bold",
            },
            alternateRowStyles: {
                fillColor: [247, 250, 248],
            },
            didParseCell: (hookData) => {
                if (hookData.section !== "foot") {
                    return;
                }

                if (hookData.column.index >= headersRow.length - 3) {
                    hookData.cell.styles.halign = "right";
                }
            },
            columnStyles,
        });
    };

    const buildMainPdfData = (items) => {
        let totalDebit = 0;
        let totalCredit = 0;
        let totalBalance = 0;

        const bodyRows = items.map((item, index) => {
            totalDebit += Number(item.Debit || 0);
            totalCredit += Number(item.Credit || 0);
            totalBalance += Math.abs(Number(item.Balance || 0));

            return [
                String(index + 1),
                item.LedgerName || "",
                item.BilanceSide || "",
                formatFinancialStatement(item.FinancialStatements || ""),
                formatCurrency(item.Debit),
                formatCurrency(item.Credit),
                formatUnsignedCurrency(item.Balance),
            ];
        });

        const footRow = ["", "", "", "Totali", formatCurrency(totalDebit), formatCurrency(totalCredit), formatUnsignedCurrency(totalBalance)];
        return { bodyRows, footRow };
    };

    const buildDetailsPdfData = (items) => {
        let totalDebit = 0;
        let totalCredit = 0;
        let totalBalance = 0;

        const bodyRows = items.map((item, index) => {
            totalDebit += Number(item.Debit || 0);
            totalCredit += Number(item.Credit || 0);
            totalBalance += Number(item.Debit || 0) - Number(item.Credit || 0);

            return [
                String(index + 1),
                getActionTypeLabel(item.ActionType || ""),
                item.LedgerNameSQ || "",
                item.Comment || "",
                formatDateForDisplay(item.Date),
                formatCurrency(item.Debit),
                formatCurrency(item.Credit),
                formatCurrency(item.Progressive),
            ];
        });

        const footRow = ["", "", "", "Totali", "", formatCurrency(totalDebit), formatCurrency(totalCredit), formatCurrency(totalBalance)];
        return { bodyRows, footRow };
    };

    const buildMainPdfDocument = async () => {
        const items = await fetchAllRows();
        if (!items.length) {
            errorAlert("Nuk ka të dhëna për eksport.");
            return null;
        }

        const doc = new jsPDF({ orientation: "landscape" });
        const generatedOnLabel = formatDateForDisplay(new Date());
        const startY = addReportHeader(
            doc,
            "Bilanci Vërtetues",
            "Raport financiar për verifikimin e bilancit të llogarive",
            reportPeriodLabel,
        );
        const { bodyRows, footRow } = buildMainPdfData(items);

        addPdfTable(
            doc,
            ["#", "Emri i llogarisë", "Bilanci Kontabël", "Pasqyrimi financiar", "Debi", "Kredi", "Bilanci"],
            bodyRows,
            footRow,
            startY,
            {
                0: { cellWidth: 8, halign: "center" },
                1: { cellWidth: 40 },
                2: { cellWidth: 20 },
                3: { cellWidth: 22 },
                4: { cellWidth: 16, halign: "right" },
                5: { cellWidth: 16, halign: "right" },
                6: { cellWidth: 16, halign: "right" },
            },
        );

        addReportFooter(doc, reportPeriodLabel, generatedOnLabel);

        return {
            doc,
            companyNameForFile: sanitizeFileName(companiesData?.[0]?.CompanyName, "Kompania"),
        };
    };

    const buildDetailsPdfDocument = async () => {
        const detailItems = await fetchAllDetailsRows();
        if (!detailItems.length) {
            errorAlert("Nuk ka të dhëna për eksport.");
            return null;
        }

        const doc = new jsPDF({ orientation: "landscape" });
        const generatedOnLabel = formatDateForDisplay(new Date());
        const accountHeaderLabel = accountDetailName ? `Llogaria: ${accountDetailName}` : "";
        const startY = addReportHeader(
            doc,
            "Bilanci Vërtetues - Detale",
            "Raport i lëvizjeve për llogarinë e zgjedhur",
            reportPeriodLabel,
            accountHeaderLabel,
        );
        const { bodyRows, footRow } = buildDetailsPdfData(detailItems);

        addPdfTable(
            doc,
            ["#", "Lloji", "Llogaria", "Përshkrimi", "Data", "Debi", "Kredi", "Progresive"],
            bodyRows,
            footRow,
            startY,
            {
                0: { cellWidth: 8, halign: "center" },
                1: { cellWidth: 12 },
                2: { cellWidth: 26 },
                3: { cellWidth: 36 },
                4: { cellWidth: 14, halign: "center" },
                5: { cellWidth: 14, halign: "right" },
                6: { cellWidth: 14, halign: "right" },
                7: { cellWidth: 16, halign: "right" },
            },
        );

        addReportFooter(doc, reportPeriodLabel, generatedOnLabel);

        return {
            doc,
            accountNameForFile: sanitizeFileName(accountDetailName, "Llogaria_Detaje"),
        };
    };

    const exportMainPdf = async () => {
        const generatedDocument = await buildMainPdfDocument();
        if (!generatedDocument) {
            return;
        }

        const { doc, companyNameForFile } = generatedDocument;
        doc.save(`Bilanci_Vertetues_${fiscalYear}_${companyNameForFile}.pdf`);
    };

    const exportDetailsPdf = async () => {
        const generatedDocument = await buildDetailsPdfDocument();
        if (!generatedDocument) {
            return;
        }

        const { doc, accountNameForFile } = generatedDocument;
        doc.save(`${accountNameForFile}_Detaje.pdf`);
    };

    const printPdfDocument = (doc) => {
        const pdfBlob = doc.output("blob");
        const printUrl = URL.createObjectURL(pdfBlob);
        const printFrame = document.createElement("iframe");
        printFrame.style.position = "fixed";
        printFrame.style.left = "-10000px";
        printFrame.style.top = "0";
        printFrame.style.width = "1024px";
        printFrame.style.height = "768px";
        printFrame.style.border = "0";
        printFrame.style.opacity = "0";
        printFrame.style.pointerEvents = "none";
        let didPrint = false;
        let cleanupDone = false;

        const cleanup = () => {
            if (cleanupDone) {
                return;
            }

            cleanupDone = true;
            URL.revokeObjectURL(printUrl);
            if (printFrame.parentNode) {
                printFrame.parentNode.removeChild(printFrame);
            }
        };

        printFrame.onload = () => {
            if (didPrint) {
                return;
            }

            didPrint = true;

            try {
                const frameWindow = printFrame.contentWindow;
                if (!frameWindow) {
                    cleanup();
                    errorAlert("Printimi nuk u inicializua. Ju lutem provoni përsëri.");
                    return;
                }

                window.setTimeout(() => {
                    frameWindow.focus();
                    frameWindow.onafterprint = cleanup;
                    frameWindow.print();
                    window.setTimeout(cleanup, 5000);
                }, 450);
            } catch (error) {
                cleanup();
                errorAlert("Nuk u hap dialogu i printimit. Ju lutem provoni përsëri.");
            }
        };

        document.body.appendChild(printFrame);
        printFrame.src = printUrl;

        window.setTimeout(() => {
            cleanup();
        }, 60000);
    };

    const handlePrintMainPdf = async () => {
        const generatedDocument = await buildMainPdfDocument();
        if (!generatedDocument) {
            return;
        }

        printPdfDocument(generatedDocument.doc);
    };

    const handlePrintDetailsPdf = async () => {
        const generatedDocument = await buildDetailsPdfDocument();
        if (!generatedDocument) {
            return;
        }

        printPdfDocument(generatedDocument.doc);
    };

    const handleExportMainXlsx = async () => {
        const items = await fetchAllRows();
        exportDatasetToExcel(
            mapMainExportData(items),
            financialColumns,
            financialMappings,
            `Bilanci_Vertetues_${fiscalYear}`,
        );
    };

    const handleExportDetailsXlsx = async () => {
        const detailItems = await fetchAllDetailsRows();
        exportDatasetToExcel(
            mapDetailsExportData(detailItems),
            financialDetailColumns,
            financialDetailMappings,
            `${accountDetailName || "Llogaria"}_Detaje`,
        );
    };

    const mainColumns = useMemo(() => ([
        { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
        { field: "ledgerName", headerName: "Emri i llogarisë", minWidth: 280, width: 360 },
        { field: "debit", headerName: "Debi", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "credit", headerName: "Kredi", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "balance", headerName: "Bilanci", minWidth: 130, width: 150, valueFormatter: (value) => formatUnsignedCurrency(value) },
        { field: "bilanceSide", headerName: "Bilanci Kontabël", minWidth: 170, width: 200 },
        { field: "financialStatement", headerName: "Pasqyrimi financiar", minWidth: 190, width: 220 },
    ]), []);

    const rows = useMemo(() => (
        data.map((item, index) => ({
            id: item.LedgerID || `trial-balance-${index}`,
            rowNumber: paginationModel.page * paginationModel.pageSize + index + 1,
            ledgerName: item.LedgerName || "",
            debit: item.Debit || 0,
            credit: item.Credit || 0,
            balance: Math.abs(Number(item.Balance || 0)),
            bilanceSide: item.BilanceSide || "",
            financialStatement: formatFinancialStatement(item.FinancialStatements || ""),
            LedgerID: item.LedgerID,
            LedgerName: item.LedgerName,
            rowInfo: item,
        }))
    ), [data, paginationModel.page, paginationModel.pageSize]);

    const mainSummaryFooter = useMemo(() => ({
        labelField: "ledgerName",
        label: "Totali",
        values: {
            debit: formatCurrency(totals.totalDebit),
            credit: formatCurrency(totals.totalCredit),
            balance: formatUnsignedCurrency(totals.totalBalance),
        },
    }), [totals]);

    const detailColumns = useMemo(() => ([
        { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
        { field: "type", headerName: "Lloji", minWidth: 120, width: 140 },
        { field: "ledgerNameSQ", headerName: "Llogaria", minWidth: 240, width: 300 },
        { field: "comment", headerName: "Përshkrimi", minWidth: 220, width: 280 },
        { field: "date", headerName: "Data", minWidth: 120, width: 140 },
        { field: "debit", headerName: "Debi", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "credit", headerName: "Kredi", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "progressive", headerName: "Progresive", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
    ]), []);

    const detailRows = useMemo(() => (
        accountDetails.map((item, index) => ({
            id: item.JournalID || `trial-balance-detail-${index}`,
            rowNumber: detailsPaginationModel.page * detailsPaginationModel.pageSize + index + 1,
            type: getActionTypeLabel(item.ActionType || ""),
            ledgerNameSQ: item.LedgerNameSQ || "",
            comment: item.Comment || "",
            date: formatDateForDisplay(item.Date),
            debit: item.Debit || 0,
            credit: item.Credit || 0,
            progressive: item.Progressive || 0,
            rowInfo: item,
        }))
    ), [accountDetails, detailsPaginationModel.page, detailsPaginationModel.pageSize]);

    const detailsSummaryFooter = useMemo(() => ({
        labelField: "date",
        label: "Totali",
        values: {
            debit: formatCurrency(detailsTotals.debit),
            credit: formatCurrency(detailsTotals.credit),
            progressive: formatCurrency(detailsTotals.balance),
        },
    }), [detailsTotals]);

    return (
        <div className="Crud__Container ARP__page">
            <div className="ARP__content">
                {!showDetails && (
                    <>
                        <CrudPageHeader
                            icon={FaBalanceScale}
                            title="Bilanci Vërtetues"
                            description="Kjo formë është zhvilluar për të vërifikuar bilancin e llogarive kontabel."
                            className="ARP__header"
                        />

                        <div className="ARP__reportGrid">
                            <ReusableAccountBooksGrid
                                columns={mainColumns}
                                rows={rows}
                                loading={loading}
                                rowCount={rowCount}
                                height={650}
                                paginationModel={paginationModel}
                                setPaginationModel={setPaginationModel}
                                sortModel={sortModel}
                                setSortModel={setSortModel}
                                filterModel={filterModel}
                                setFilterModel={setFilterModel}
                                quickFilter={quickFilter}
                                setQuickFilter={setQuickFilter}
                                showViewAction={true}
                                onView={handleViewDetails}
                                showEditAction={false}
                                showDeleteAction={false}
                                showExportButton={false}
                                summaryFooter={mainSummaryFooter}
                                toolbarActions={(
                                    <>
                                        <AddActionsButton
                                            text="Print"
                                            onClick={handlePrintMainPdf}
                                            icon={<AiOutlinePrinter />}
                                            className="RSDG__toolButton ARP__gridActionBtn--print"
                                        />
                                        <AddActionsButton
                                            text="PDF"
                                            onClick={exportMainPdf}
                                            icon={<AiOutlineFilePdf />}
                                            className="RSDG__toolButton ARP__gridActionBtn--pdf"
                                        />
                                        <AddActionsButton
                                            text="XLSX"
                                            onClick={handleExportMainXlsx}
                                            icon={<BsFiletypeXls />}
                                            className="RSDG__toolButton ARP__gridActionBtn--xlsx"
                                        />
                                    </>
                                )}
                            />
                        </div>
                    </>
                )}

                {showDetails && (
                    <>
                        <CrudPageHeader
                            icon={FaBalanceScale}
                            title={`Llogaria në Detale${accountDetailName ? ` - ${accountDetailName}` : ""}`}
                            description="Shfaqen të gjitha lëvizjet për llogarinë e zgjedhur."
                            className="ARP__header"
                            action={(
                                <AddActionsButton
                                    text="Kthehu te lista"
                                    onClick={() => {
                                        setShowDetails(false);
                                        setSelectedLedgerId(null);
                                        setAccountDetails([]);
                                        setDetailsRowCount(0);
                                        setDetailsTotals({ debit: 0, credit: 0, balance: 0 });
                                        setDetailsPaginationModel({ page: 0, pageSize: 100 });
                                        setDetailsSortModel([]);
                                        setDetailsFilterModel({ items: [] });
                                        setDetailsQuickFilter([]);
                                        setAccountDetailName("");
                                    }}
                                    icon={<FiArrowLeft />}
                                    className="ARP__backBtn"
                                />
                            )}
                        />

                        <div className="ARP__reportGrid ARP__reportGrid--details">
                            <ReusableAccountBooksGrid
                                columns={detailColumns}
                                rows={detailRows}
                                loading={detailsLoading}
                                rowCount={detailsRowCount}
                                height={650}
                                paginationModel={detailsPaginationModel}
                                setPaginationModel={setDetailsPaginationModel}
                                sortModel={detailsSortModel}
                                setSortModel={setDetailsSortModel}
                                filterModel={detailsFilterModel}
                                setFilterModel={setDetailsFilterModel}
                                quickFilter={detailsQuickFilter}
                                setQuickFilter={setDetailsQuickFilter}
                                showViewAction={false}
                                showEditAction={false}
                                showDeleteAction={false}
                                showExportButton={false}
                                summaryFooter={detailsSummaryFooter}
                                toolbarActions={(
                                    <>
                                        <AddActionsButton
                                            text="Print"
                                            onClick={handlePrintDetailsPdf}
                                            icon={<AiOutlinePrinter />}
                                            className="RSDG__toolButton ARP__gridActionBtn--print"
                                        />
                                        <AddActionsButton
                                            text="PDF"
                                            onClick={exportDetailsPdf}
                                            icon={<AiOutlineFilePdf />}
                                            className="RSDG__toolButton ARP__gridActionBtn--pdf"
                                        />
                                        <AddActionsButton
                                            text="XLSX"
                                            onClick={handleExportDetailsXlsx}
                                            icon={<BsFiletypeXls />}
                                            className="RSDG__toolButton ARP__gridActionBtn--xlsx"
                                        />
                                    </>
                                )}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}